import { API_URL, fetchApi } from './client';

/**
 * Get city suggestions based on query
 */
export const getCitySuggestions = async (query: string, language: string = 'en'): Promise<any[]> => {
  return fetchApi<any[]>(`${API_URL}/geocoding/suggestions?query=${encodeURIComponent(query)}&language=${language}`);
};

/**
 * Calculate distance between cities
 */
export const calculateDistance = async (
  originCity: string,
  destinationCity: string,
  language: string = 'en',
  waypoints?: string[]
): Promise<any> => {
  return fetchApi<any>(`${API_URL}/geocoding/distance`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      originCity,
      destinationCity,
      language,
      waypoints
    })
  });
};

/**
 * Distance by coordinates (MapQuest via backend)
 */
export const getDistanceByCoordinates = async (
  originLat: number,
  originLng: number,
  destLat: number,
  destLng: number,
  language: string = 'en'
): Promise<any> => {
  const url = `${API_URL}/geocoding/distance?origin_lat=${originLat}&origin_lng=${originLng}&dest_lat=${destLat}&dest_lng=${destLng}&language=${language}`;
  const data = await fetchApi<any>(url);
  return {
    distance: data.distance,
    formattedDistance: data.formatted_distance,
    duration: data.duration,
    formattedDuration: data.formatted_duration,
    roadNumbers: data.road_numbers ?? data.roadNumbers ?? [],
    legDistances: data.leg_distances ?? data.legDistances ?? [],
  };
};

/**
 * Distance by city names (MapQuest via backend)
 */
export const getDistanceByCities = async (
  originCity: string,
  destinationCity: string,
  language: string = 'en'
): Promise<any> => {
  const url = `${API_URL}/geocoding/distance-by-cities?origin_city=${encodeURIComponent(originCity)}&destination_city=${encodeURIComponent(destinationCity)}&language=${language}`;
  const data = await fetchApi<any>(url);
  return {
    distance: data.distance,
    formattedDistance: data.formatted_distance,
    duration: data.duration,
    formattedDuration: data.formatted_duration,
    roadNumbers: data.road_numbers ?? data.roadNumbers ?? [],
    legDistances: data.leg_distances ?? data.legDistances ?? [],
  };
};

/**
 * Geocode location name to coordinates (backend: ORS + OpenCage fallback).
 * При 502/ошибке возвращаем null, чтобы не плодить повторы — фронт может использовать другой источник.
 */
export const getGeocode = async (
  text: string,
  lang: string = 'en'
): Promise<{ lng: number; lat: number } | null> => {
  if (!text?.trim()) return null;
  try {
    const res = await fetch(`${API_URL}/geocoding/geocode?text=${encodeURIComponent(text.trim())}&lang=${encodeURIComponent(lang)}`, {
      credentials: 'include',
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data && typeof data.lng === 'number' && typeof data.lat === 'number' ? data : null;
  } catch {
    return null;
  }
};

/**
 * Reverse geocode: get country name for coordinates (backend: OpenCage).
 * Used for border crossing detection.
 */
export const getCountryForCoordinate = async (
  lat: number,
  lng: number,
  lang: string = 'en'
): Promise<string | null> => {
  try {
    const res = await fetch(
      `${API_URL}/geocoding/reverse?lat=${lat}&lng=${lng}&lang=${encodeURIComponent(lang)}`,
      { credentials: 'include' }
    );
    if (!res.ok) return null;
    const data = await res.json();
    const c = data?.country;
    return (typeof c === 'string' && c !== '') ? c : null;
  } catch {
    return null;
  }
};

export type RouteOptions = {
  avoidFerries?: boolean;
  avoidToll?: boolean;
};

/**
 * Построение маршрута по координатам [[lng, lat], ...] через бэкенд (ORS).
 */
export const getRoute = async (
  coordinates: [number, number][],
  language: string = 'en',
  options?: RouteOptions
): Promise<any> => {
  if (!coordinates?.length || coordinates.length < 2) throw new Error('At least 2 coordinates required');
  const body: Record<string, unknown> = { coordinates, language };
  if (options?.avoidFerries) body.avoid_ferries = true;
  if (options?.avoidToll) body.avoid_toll = true;
  const res = await fetch(`${API_URL}/geocoding/route`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Route failed: ${res.status} ${text.slice(0, 100)}`);
  }
  return res.json();
};
